import React from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "@mui/material";
import logoImage from "images/rs-logo.png";
import paidStamp from "images/paid_stamp.png";
import { USDollar } from "utils/helpers";

const HEADER_START_POSIION = 90;
const MARGIN = 50;
const INVOICE_DATA_MARGIN_LEFT = 410;

const BILL_TO_START_POSIION = 170;
const TABLE_POSIION = 220;

export const InvoiceToPdfButton = ({ data }) => {
  const { invoice, recipient } = data;
  const isPaid = data?.invoice?.paymentStatus === "Paid";
  const positions = data?.invoiceItems ?? [];
  const taxes = data?.invoiceTaxes ?? [];
  const invoiceTotal = Number(invoice?.total_amount ?? 0);

  let subTotal = 0;

  const date = new Date(data?.invoice?.invoice_date);
  const invoiceDate = new Intl.DateTimeFormat("en-US", {
    dateStyle: "short",
  }).format(date);
  date.setDate(date.getDate() + 7);
  const invoiceDueDate = new Intl.DateTimeFormat("en-US", {
    dateStyle: "short",
  }).format(date);

  const downloadPdf = () => {
    if (!invoice?.id) return;

    try {
      const pdf = new jsPDF("p", "px", "letter");
      pdf.setFontSize(12);
      pdf.text(
        [
          "Referral Stream Solutions, LLC",
          "5900 Balcones Drive",
          "Suite 400",
          "Austin, TX 78731",
        ],
        MARGIN,
        HEADER_START_POSIION
      );
      pdf.text(
        [
          `Invoice #${invoice?.id}`,
          `Invoice Date: ${invoiceDate}`,
          `Due Date: ${invoiceDueDate}`,
          `Total Due: ${USDollar.format(invoiceTotal)}`,
        ],
        INVOICE_DATA_MARGIN_LEFT,
        HEADER_START_POSIION,
        { align: "right" }
      );
      pdf.text(
        [
          "Bill to:",
          `${recipient?.name}`,
          `${recipient?.street} ${
            recipient?.street2 ? `, ${recipient?.street2}` : ""
          }, ${recipient?.city}, ${recipient?.state}, ${recipient?.zipCode}`,
        ],
        MARGIN,
        BILL_TO_START_POSIION
      );

      const columns = [
        {
          content: "Description",
          styles: {
            halign: "left",
          },
        },
        "Quantity",
        {
          content: "Price",
          styles: {
            halign: "right",
          },
        },
        {
          content: "Total",
          styles: {
            halign: "right",
          },
        },
      ];
      const rows = [];

      positions.forEach(
        ({
          item_description,
          quantity,
          unit_price,
          unit_price_adjusted,
          total_price,
          total_price_adjusted,
        }) => {
          const price = Number(unit_price ?? 0);
          const priceAdjusted = Number(unit_price_adjusted ?? 0);

          const totalPrice = Number(total_price ?? 0);
          const totalPriceAdjusted = Number(total_price_adjusted ?? 0);

          subTotal += totalPriceAdjusted > 0 ? totalPriceAdjusted : totalPrice;

          rows.push([
            item_description,
            quantity,
            USDollar.format(priceAdjusted > 0 ? priceAdjusted : price),
            USDollar.format(
              totalPriceAdjusted > 0 ? totalPriceAdjusted : totalPrice
            ),
          ]);
        }
      );

      rows.push([
        {
          content: "Sub Total:",
          colSpan: 3,
          styles: {
            halign: "right",
          },
        },
        USDollar.format(subTotal),
      ]);

      taxes?.forEach(({ description, rate, amount }) => {
        rows.push([
          {
            content: `${description} (${rate}%)`,
            colSpan: 3,
            styles: {
              halign: "right",
            },
          },
          USDollar.format(amount),
        ]);
      });

      rows.push([
        {
          content: "Invoice Total:",
          colSpan: 3,
          styles: {
            halign: "right",
          },
        },
        USDollar.format(invoiceTotal),
      ]);

      autoTable(pdf, {
        startY: TABLE_POSIION, // Adjust the starting y-position of the table
        margin: {
          left: MARGIN,
          right: MARGIN,
        },
        // @ts-ignore
        head: [columns],
        // @ts-ignore
        body: rows,
        headStyles: {
          halign: "center",
          fillColor: "#225DA4",
        },
        columnStyles: {
          1: { halign: "center" }, // Align Quantity to the center
          2: { halign: "right" }, // Align Price to the right
          3: { halign: "right" }, // Align Total to the right
        },
        theme: "grid", // You can change the theme if needed
      });

      const image = new Image();
      image.src = logoImage;
      pdf.addImage(image, "PNG", MARGIN - 5, 30, 93, 43);

      if (isPaid) {
        const image = new Image();
        image.src = paidStamp;
        pdf.addImage(image, "PNG", 250, 0, 100, 100);
      }

      const blob = pdf.output("blob");
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `referral_stream_invoice_${invoice?.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {}
  };

  return (
    <Button
      sx={{ padding: "5px 10px" }}
      variant="contained"
      color={"info"}
      onClick={downloadPdf}
    >
      Download PDF
    </Button>
  );
};
